import { ArenaProfile } from 'shared/contexts/ArenaProfileContext';
import { areaDefinition, DATE_BASIS_LABEL, FINANCIAL_DATE_BASIS, TIME_BAND_OPTIONS, WEEKDAY_OPTIONS } from 'modules/arena/reports/model/areas';
import { formatDate, formatPeriod, todayIn } from 'modules/arena/reports/model/dates';
import { formatMoney, formatPercent, formatRating } from 'modules/arena/reports/model/format';
import {
  AreaResponse,
  CellValue,
  DateBasis,
  DetailResponse,
  ExportState,
  ReportArea,
  ReportFilters,
  ReportMeta,
  ReportPermissions,
  RatioValue,
  StatusValue,
  TableColumn,
  TablePage,
  TableQuery,
  TableRow,
} from 'modules/arena/reports/model/types';
import {
  AggregateContext,
  buildClasses,
  buildCourts,
  buildFinancial,
  buildInstructors,
  buildOverview,
  buildPackages,
  buildTournaments,
  classRows,
  courtRows,
  financialDetail,
  financialRows,
  instructorRows,
  packageRows,
  PAGE_SIZE,
  previousRange,
} from 'modules/arena/reports/source/demo/aggregate';
import { DEMO_MIN_DATE, DEMO_TIMEZONE, DemoDb, getDemoDb } from 'modules/arena/reports/source/demo/db';
import { ReportContext, ReportsSource } from 'modules/arena/reports/source/types';

export type DemoScenario = 'normal' | 'gestor' | 'vazio' | 'parcial' | 'lento' | 'erro' | 'desconectado' | 'negado';

export const DEMO_SCENARIOS: { id: DemoScenario; label: string }[] = [
  { id: 'normal', label: 'Normal (proprietário)' },
  { id: 'gestor', label: 'Gestor com acesso limitado' },
  { id: 'vazio', label: 'Sem movimentação' },
  { id: 'parcial', label: 'Dados parciais' },
  { id: 'lento', label: 'Respostas lentas e fora de ordem' },
  { id: 'erro', label: 'Erro na consulta' },
  { id: 'desconectado', label: 'Fonte não conectada' },
  { id: 'negado', label: 'Acesso revogado' },
];

const AREA_DATE_BASIS: Record<ReportArea, DateBasis | 'misto'> = {
  'visao-geral': 'misto',
  financeiro: 'venda',
  aulas: 'aula',
  professores: 'avaliacao',
  pacotes: 'contrato',
  quadras: 'reserva',
  torneios: 'inscricao',
};

const PARTIAL = {
  message: 'Recebimentos por cartão das últimas 48 horas ainda não foram confirmados pelo processador. Os valores podem subir na próxima atualização.',
  affected: ['recebido-bruto', 'liquido', 'repassado'],
};

function wait(ms: number, signal: AbortSignal): Promise<void> {
  return new Promise((resolve, reject) => {
    const timer = window.setTimeout(resolve, ms);
    signal.addEventListener('abort', () => {
      window.clearTimeout(timer);
      reject(new DOMException('Consulta cancelada', 'AbortError'));
    });
  });
}

function textOf(value: CellValue, kind: TableColumn['kind']): string {
  if (value === null || value === undefined) return 'Indisponível';
  if (typeof value === 'object') {
    if ('pct' in value) {
      const ratio = value as RatioValue;
      return `${ratio.value} de ${ratio.capacity} (${formatPercent(ratio.pct)})`;
    }
    return (value as StatusValue).label;
  }
  if (kind === 'money' && typeof value === 'number') return (value / 100).toFixed(2).replace('.', ',');
  if (kind === 'percent' && typeof value === 'number') return formatPercent(value);
  if (kind === 'rating') return formatRating(typeof value === 'number' ? value : null);
  if (kind === 'date' && typeof value === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(value)) return formatDate(value);
  return String(value);
}

function toCsv(lines: string[][]): string {
  return lines.map((line) => line.map((cell) => (/[";\n]/.test(cell) ? `"${cell.replace(/"/g, '""')}"` : cell)).join(';')).join('\n');
}

export function createDemoSource(profile: ArenaProfile, isCustom: boolean, scenario: DemoScenario): ReportsSource {
  const today = todayIn(DEMO_TIMEZONE);
  const db: DemoDb = getDemoDb(profile, isCustom, today);
  const latency = () => (scenario === 'lento' ? 400 + Math.random() * 2200 : 350 + Math.random() * 300);

  const permissions: ReportPermissions =
    scenario === 'gestor'
      ? {
          areas: ['visao-geral', 'aulas', 'professores', 'quadras', 'torneios'],
          unitIds: [db.units[0].id],
          canSeeStudentNames: false,
          canSeeComments: false,
          canExport: false,
        }
      : {
          areas: ['visao-geral', 'financeiro', 'aulas', 'professores', 'pacotes', 'quadras', 'torneios'],
          unitIds: db.units.map((unit) => unit.id),
          canSeeStudentNames: true,
          canSeeComments: true,
          canExport: true,
        };

  const visibleUnits = db.units.filter((unit) => permissions.unitIds.includes(unit.id));
  const context: ReportContext = {
    permissions,
    catalog: {
      arenaKey: db.arenaKey,
      arenaName: db.arenaName,
      timezone: DEMO_TIMEZONE,
      units: visibleUnits,
      sports: db.sports.map((sport) => ({ id: sport, label: sport })),
      options: {
        instructors: db.instructors.map((item) => ({ id: item.id, label: item.label, sports: item.sports })),
        classGroups: db.groups.map((group) => ({ id: group.id, label: group.label, sports: [group.sport], unitId: group.unitId, parentId: group.instructorId })),
        levels: ['Iniciante', 'Intermediário', 'Avançado'].map((level) => ({ id: level, label: level })),
        products: [
          { id: 'reserva', label: 'Reservas de quadra' },
          { id: 'aula-avulsa', label: 'Aulas avulsas' },
          { id: 'pacote', label: 'Pacotes mensais' },
          ...(db.tournaments.length ? [{ id: 'torneio', label: 'Inscrições em torneios' }] : []),
        ],
        paymentStatuses: [
          { id: 'pago', label: 'Pago' },
          { id: 'parcial', label: 'Parcialmente pago' },
          { id: 'pendente', label: 'A receber' },
          { id: 'vencido', label: 'Vencido' },
          { id: 'cancelado', label: 'Cancelado' },
          { id: 'reembolso-solicitado', label: 'Reembolso solicitado' },
          { id: 'reembolsado', label: 'Reembolsado' },
        ],
        paymentMethods: [
          { id: 'pix', label: 'Pix' },
          { id: 'cartao', label: 'Cartão de crédito' },
        ],
        plans: db.plans.map((plan) => ({ id: plan.id, label: `${plan.label} · ${plan.frequencyLabel}` })),
        contractStatuses: [
          { id: 'ativo', label: 'Ativo' },
          { id: 'renovacao-pendente', label: 'Renovação pendente' },
          { id: 'vencido', label: 'Vencido' },
          { id: 'cancelado', label: 'Cancelado' },
        ],
        courts: db.courts.map((court) => ({ id: court.id, label: court.label, sports: court.sports, unitId: court.unitId })),
        tournaments: db.tournaments.map((t) => ({ id: t.id, label: t.label, sports: [t.sport], unitId: t.unitId })),
        categories: db.tournaments.flatMap((t) => t.categories.map((c) => ({ id: c.id, label: `${c.label} (${t.label})`, sports: [t.sport], parentId: t.id }))),
        registrationStatuses: [
          { id: 'confirmada', label: 'Confirmada' },
          { id: 'pendente', label: 'Pagamento pendente' },
          { id: 'cancelada', label: 'Cancelada' },
          { id: 'lista-espera', label: 'Lista de espera' },
        ],
      },
    },
  };

  const ctxFor = (filters: ReportFilters): AggregateContext => ({ db, filters, permissions, today });
  const range = (filters: ReportFilters) => ({ start: filters.common.startDate, end: filters.common.endDate });

  function meta(area: ReportArea, filters: ReportFilters): ReportMeta {
    const dateBasis = area === 'financeiro' ? ((filters.specific.dateBasis || 'venda') as DateBasis) : AREA_DATE_BASIS[area];
    const updated = new Date(Date.now() - (3 + (filters.common.startDate.charCodeAt(9) % 6)) * 60_000);
    return {
      period: range(filters),
      previousPeriod: filters.common.compare ? previousRange(range(filters)) : null,
      timezone: DEMO_TIMEZONE,
      dateBasis,
      referenceDate: today,
      updatedAt: updated.toISOString(),
      inProgress: filters.common.endDate >= today,
      partial: scenario === 'parcial' && (area === 'visao-geral' || area === 'financeiro') ? PARTIAL : null,
    };
  }

  /** Há movimento nesta área? (com ou sem os filtros do usuário) */
  function activity(area: ReportArea, filters: ReportFilters): boolean {
    const ctx = ctxFor(filters);
    const r = range(filters);
    const q = { page: 1 };
    switch (area) {
      case 'visao-geral': {
        const body = buildOverview(ctx, r, q);
        return body.metrics.some((m) => typeof m.value === 'number' && m.value !== 0);
      }
      case 'financeiro':
        return financialRows(ctx, r).rows.length > 0;
      case 'aulas':
        return classRows(ctx, r, 'ocupacao').length > 0;
      case 'professores':
        return instructorRows(ctx, r, 'nota').some((row) => Number(row.cells.count) > 0 || Number(row.cells.classes) > 0);
      case 'pacotes':
        return packageRows(ctx, r).length > 0;
      case 'quadras':
        return courtRows(ctx, r).length > 0;
      case 'torneios':
        return buildTournaments(ctx, r, q).table.totalRows > 0;
    }
  }

  function body(area: ReportArea, filters: ReportFilters, query: TableQuery) {
    const ctx = ctxFor(filters);
    const r = range(filters);
    switch (area) {
      case 'visao-geral':
        return buildOverview(ctx, r, query);
      case 'financeiro':
        return buildFinancial(ctx, r, query);
      case 'aulas':
        return buildClasses(ctx, r, query);
      case 'professores':
        return buildInstructors(ctx, r, query);
      case 'pacotes':
        return buildPackages(ctx, r, query);
      case 'quadras':
        return buildCourts(ctx, r, query);
      case 'torneios':
        return buildTournaments(ctx, r, query);
    }
  }

  function markPartial<T>(value: T): T {
    if (scenario !== 'parcial') return value;
    const json = JSON.stringify(value);
    const marked = JSON.parse(json, (key, item) =>
      item && typeof item === 'object' && 'polarity' in item && PARTIAL.affected.includes(item.id) ? { ...item, partial: true } : item
    );
    return marked;
  }

  function unfiltered(filters: ReportFilters): ReportFilters {
    return { common: { ...filters.common, unitIds: [], sport: '' }, specific: { dateBasis: filters.specific.dateBasis ?? '' } };
  }

  return {
    id: `demo:${db.arenaKey}:${scenario}`,
    capabilities: {
      minDate: DEMO_MIN_DATE,
      maxRangeDays: 366,
      compare: true,
      grouping: true,
      export: { csv: true, pdf: false, expiresInMinutes: 15 },
      demo: true,
    },

    async loadContext(signal) {
      await wait(250, signal);
      if (scenario === 'desconectado') return { status: 'not_connected', message: 'A integração de relatórios ainda não está conectada a este ambiente.' };
      return { status: 'ok', context };
    },

    async fetchArea(area, filters, query, signal) {
      await wait(latency(), signal);
      if (scenario === 'erro') throw new Error('Não foi possível consultar os relatórios agora (tempo de resposta excedido).');
      if (scenario === 'negado' || !permissions.areas.includes(area)) {
        return { status: 'denied', meta: null, message: 'Seu acesso a este relatório foi removido ou não inclui esta área.', body: null } as AreaResponse;
      }
      const m = meta(area, filters);
      if (scenario === 'vazio' || !activity(area, unfiltered(filters))) {
        return { status: 'empty', meta: m, message: 'Não houve movimentação neste período.', body: null };
      }
      if (!activity(area, filters)) {
        return { status: 'no_results', meta: m, message: 'Nenhum resultado com os filtros aplicados.', body: null };
      }
      return { status: 'ok', meta: m, body: markPartial(body(area, filters, query)) as unknown as Record<string, unknown> };
    },

    async fetchTable(area, filters, query, signal) {
      await wait(latency() * 0.6, signal);
      if (scenario === 'erro') throw new Error('Não foi possível carregar a tabela.');
      const response = body(area, filters, query) as { table: TablePage };
      return response.table;
    },

    async fetchDetail(area, rowId, signal) {
      await wait(300, signal);
      if (area !== 'financeiro') return null;
      const detail = financialDetail(ctxFor({ common: { preset: 'mes-atual', startDate: today, endDate: today, unitIds: [], sport: '', grouping: 'dia', compare: false }, specific: {} }), rowId);
      if (!detail) return null;
      const { order, fees, charged, net } = detail;
      const statusLabel: Record<string, string> = {
        pago: 'Pago',
        parcial: 'Parcialmente pago',
        pendente: 'A receber',
        vencido: 'Vencido',
        cancelado: 'Cancelado — sem reembolso',
        'reembolso-solicitado': 'Reembolso solicitado (aguardando confirmação)',
        reembolsado: 'Reembolso concluído',
      };
      const response: DetailResponse = {
        title: `Pedido ${order.id}`,
        subtitle: `${order.description} · ${db.units.find((unit) => unit.id === order.unitId)?.label ?? ''}`,
        sections: [
          {
            title: 'Valores do pedido',
            rows: [
              { label: 'Valor bruto (preço)', value: formatMoney(order.grossCents) },
              { label: 'Desconto', value: order.discountCents ? formatMoney(-order.discountCents) : formatMoney(0), tone: order.discountCents ? 'danger' : 'muted' },
              { label: 'Cobrado do cliente', value: formatMoney(charged) },
              ...(fees.processorClient || order.processorFeePaidByClient
                ? [{ label: 'Taxa de serviço paga pelo cliente', value: formatMoney(Math.round(order.processorFeeCents)), note: 'Cobrada do cliente além do pedido. Não sai do valor da arena.' }]
                : []),
            ],
          },
          {
            title: 'Recebimento',
            rows: [
              { label: 'Situação', value: statusLabel[order.status] },
              { label: 'Recebido', value: formatMoney(order.paidCents), tone: order.paidCents ? 'success' : 'muted' },
              ...(charged - order.paidCents > 0 && order.status !== 'cancelado'
                ? [{ label: 'Saldo pendente', value: formatMoney(charged - order.paidCents), tone: 'danger' as const }]
                : []),
              { label: 'Meio de pagamento', value: order.method === 'pix' ? 'Pix' : 'Cartão de crédito' },
            ],
          },
          {
            title: 'Taxas que cabem à arena',
            rows: [
              { label: 'Taxa da plataforma', value: formatMoney(-fees.platform), tone: fees.platform ? 'danger' : 'muted', note: 'Percentual registrado no pedido na hora da venda.' },
              {
                label: 'Taxa do processador',
                value: order.processorFeePaidByClient ? 'Paga pelo cliente' : formatMoney(-fees.processorArena),
                tone: order.processorFeePaidByClient ? 'muted' : 'danger',
              },
            ],
          },
          {
            title: 'Reembolso',
            rows: [
              {
                label: order.status === 'reembolso-solicitado' ? 'Solicitado (não concluído)' : 'Concluído',
                value: order.status === 'reembolso-solicitado' ? formatMoney(charged) : formatMoney(order.refundedCents ? -order.refundedCents : 0),
                tone: order.refundedCents ? 'danger' : 'muted',
                note: order.status === 'reembolso-solicitado' ? 'Ainda não saiu do caixa; não é descontado do líquido até a confirmação.' : undefined,
              },
            ],
          },
          {
            title: 'Resultado para a arena',
            rows: [{ label: 'Líquido da arena', value: formatMoney(net), note: 'Recebido − taxas da arena − reembolsos concluídos. Não é lucro.' }],
          },
          {
            title: 'Datas',
            rows: [
              { label: 'Venda', value: formatDate(order.saleDate) },
              { label: 'Vencimento', value: formatDate(order.dueDate) },
              { label: 'Recebimento', value: order.paidDate ? formatDate(order.paidDate) : 'Não recebido' },
              { label: 'Repasse à arena', value: order.payoutDate ? formatDate(order.payoutDate) : order.paidDate ? 'Aguardando repasse' : '—' },
            ],
          },
        ],
      };
      return response;
    },

    async exportArea(area, filters, signal) {
      await wait(900 + Math.random() * 600, signal);
      if (!permissions.canExport) return { status: 'error', message: 'Seu perfil não tem permissão para exportar.' } as ExportState;
      if (scenario === 'erro') return { status: 'error', message: 'Falha ao gerar o arquivo. Tente novamente.' } as ExportState;
      // Todas as linhas do conjunto filtrado (não só a página visível).
      const first = body(area, filters, { page: 1 }) as { table: TablePage };
      const pages = Math.max(1, Math.ceil(first.table.totalRows / PAGE_SIZE));
      const rows: TableRow[] = [];
      for (let page = 1; page <= pages; page++) rows.push(...(body(area, filters, { page, sort: first.table.sort }) as { table: TablePage }).table.rows);
      const columns = first.table.columns;
      const definition = areaDefinition(area);
      const basis = meta(area, filters).dateBasis;
      const specificLabels = definition.filters
        .filter((spec) => filters.specific[spec.key])
        .map((spec) => {
          const options = spec.options ?? context.catalog.options[spec.catalogKey ?? ''] ?? [...WEEKDAY_OPTIONS, ...TIME_BAND_OPTIONS, ...FINANCIAL_DATE_BASIS];
          return `${spec.label}: ${options.find((o) => o.id === filters.specific[spec.key])?.label ?? filters.specific[spec.key]}`;
        });
      const lines: string[][] = [
        [`Relatório: ${definition.label}`],
        [`Arena: ${db.arenaName}`],
        [`Período: ${formatPeriod(filters.common.startDate, filters.common.endDate)} (${DEMO_TIMEZONE})`],
        [`Data-base: ${DATE_BASIS_LABEL[basis]}`],
        [`Filtros: ${[filters.common.sport, ...specificLabels].filter(Boolean).join(' · ') || 'nenhum'}`],
        ['DADOS DE DEMONSTRAÇÃO'],
        [],
        columns.map((column) => column.label),
        ...rows.map((row) => columns.map((column) => textOf(row.cells[column.key], column.kind))),
      ];
      return {
        status: 'ready',
        fileName: `relatorio-${area}-${filters.common.startDate}-a-${filters.common.endDate}.csv`,
        content: '﻿' + toCsv(lines),
        rowCount: rows.length,
        scope: `Todos os ${rows.length} resultados do período filtrado`,
        expiresAt: Date.now() + 15 * 60_000,
      };
    },
  };
}
